import { Slider } from 'antd';
import { ColorContext } from 'App';
import aware from 'assets/icons/AWAREICON.png';
import default1 from 'assets/icons/Default System Icon 1.png';
import default2 from 'assets/icons/Default System Icon 2.png';
import background from 'assets/icons/Default System Icon Background.png';
import classNames from 'classnames';
import { AlarmsContentWrapper, EllipsisMenu } from 'components';
import BaseAccordion from 'components/Accordions/BaseAccordion';
import BaseTable from 'components/Tables/BaseTable';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { useAlarms } from 'hooks';
import useWindow from 'hooks/useWindow';
import { useContext, useMemo, useState } from 'react';
import { BsCheck } from 'react-icons/bs';
import { FaBellSlash } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { useAcknowledgeAlarmMutation, useSilenceAlarmMutation } from 'services/alarms/alarms.api';
import 'styles/global.css';
import { alertColorScale, getSeverityLevel } from 'utils/alertUtils';

const iconMap = {
  AWARE: aware,
  'Fusion User': default1,
  'global-actions': default1,
  'Overwatch AI': default2,
  Other: background,
};

function AcknowledgeButton({ onClick, color, fontColor }) {
  return (
    <div
      className="d-flex w-100 p-3 justify-content-start align-items-center gap-3"
      onClick={onClick}
    >
      <div
        className={classNames('circle-fill', 'center-content')}
        style={{ '--fill-color': color, '--radius': '12px', color: fontColor }}
      >
        <BsCheck />
      </div>
      <p className="mb-0" style={{ color: 'black' }}>
        Acknowledge
      </p>
    </div>
  );
}

function SilenceButton({ onClick, color, fontColor }) {
  return (
    <div
      className="d-flex w-100 p-3 justify-content-start align-items-center gap-3"
      onClick={onClick}
    >
      <div
        className={classNames('circle-fill', 'center-content')}
        style={{ '--fill-color': color, '--radius': '12px', color: fontColor }}
      >
        <IoClose />
      </div>
      <p className="mb-0" style={{ color: 'black' }}>
        Silence
      </p>
    </div>
  );
}

export default function PendingAlarms() {
  const alarms = useAlarms(true);
  const theme = useContext(ColorContext);
  const { width } = useWindow();
  const [selectedAlarms, setSelectedAlarms] = useState([]);
  const [acknowledgeAlarm] = useAcknowledgeAlarmMutation();
  const [silenceAlarm] = useSilenceAlarmMutation();

  // Helper functions
  const sourceStringCorrection = (str) => str.replace('global-actions', 'Global Action');

  const timeSinceActivity = (alarmActivity) => {
    const msSinceActivity = Date.now() - new Date(alarmActivity);
    const seconds = Math.floor(msSinceActivity / 1000) % 60;
    const minutes = Math.floor(msSinceActivity / 1000 / 60) % 60;
    const hours = Math.floor(msSinceActivity / 1000 / 60 / 60) % 24;
    const days = Math.floor(msSinceActivity / 1000 / 60 / 60 / 24);
    return `
      ${days > 0 ? `${days} Days` : ''}\t
      ${hours >= 10 ? `${hours}` : `0${hours}`}:
      ${minutes >= 10 ? `${minutes}` : `0${minutes}`}:
      ${seconds >= 10 ? `${seconds}` : `0${seconds}`} hours
      `;
  };

  const shortenHeadline = (headline) => headline.split(';')[0];

  const getAlarmId = (alarm) => {
    if (alarm.ga_identifier) return `GA_${alarm.ga_identifier}`;
    if (alarm.ca_identifier) return `CA_${alarm.ca_identifier}`;
    if (alarm.cw_identifier) return `CW_${alarm.cw_identifier}`;
    return `ID_${alarm.id}`;
  };

  const keySort = (a, b) => {
    const keys = [
      'type',
      'startTime',
      'expireTime',
      'location',
      'lat',
      'lon',
      'nwsoffice',
      'alarm_id',
    ];

    for (const key of keys) {
      if (a.toLowerCase() === key) return -1;
      if (b.toLowerCase() === key) return 1;
    }

    return a.localeCompare(b);
  };

  const parseKey = (key) => {
    switch (key.toLowerCase()) {
      case 'type':
        return 'Alert Type';
      case 'nwsoffice':
        return 'Issuing Office';
      case 'starttime':
        return 'Start Time';
      case 'expiretime':
        return 'Expiration Time';
      case 'severity':
        return 'Severity';
      case 'timezone':
        return 'Time Zone';
      case 'latitude':
        return 'Latitude';
      case 'longitude':
        return 'Longitude';
      case 'location':
        return 'Location';
      default:
        return '';
    }
  };

  const getKeyValue = (key, alarm) => {
    if (key.toLowerCase() !== 'severity') {
      return (
        <>
          {width >= 992 && (
            <p className="mb-1">
              <span className="fw-bold">{parseKey(key)}:</span> {alarm.info[key]}
            </p>
          )}
        </>
      );
    }

    // Severity block w/ slider
    return (
      <div className="d-flex flex-lg-row flex-column align-items-lg-center h-auto p-0">
        <div
          className="d-flex justify-content-center align-items-center rounded-2 gap-2"
          style={{
            backgroundColor: alertColorScale(alarm.info[key]),
            width: 137,
            height: 36,
          }}
        >
          {parseKey(key)}: {getSeverityLevel(alarm.info[key])}
        </div>
        <div className="ms-2 rounded-2 bg-base pe-3" style={{ width: 275 }}>
          <Slider
            className="w-100 rounded-2"
            name="severity"
            min={0}
            max={100}
            step={1}
            disabled
            defaultValue={0 + alarm.info[key]}
          />
        </div>
      </div>
    );
  };

  // Transform alarm data for BaseTable
  const rows = useMemo(
    () =>
      alarms.alarms?.map((alarm) => ({
        ca_identifier: alarm.ca_identifier || '',
        cw_identifier: alarm.cw_identifier || '',
        ga_identifier: alarm.ga_identifier || '',
        id: alarm.id || '',
        headline: alarm.headline,
        info: alarm.info,
        message: alarm.message,
        silenced: alarm.silenced,
        state: alarm.state === 1 ? 'Active' : 'Inactive',
        source: alarm.source,
        device: alarm.device,
        type: shortenHeadline(alarm.headline),
        activity: new Date(alarm.activity).toLocaleString(),
        status: timeSinceActivity(alarm.activity),
      })),
    [alarms.alarms],
  );

  // Event Handlers
  const onAcknowledge = async (alarmIds) => {
    const ids = Array.isArray(alarmIds) ? alarmIds : [alarmIds];
    try {
      await acknowledgeAlarm(ids).unwrap();

      // Auto-silence acknowledged alarms that aren't silenced yet
      const toSilence = alarms.alarms.filter((alarm) => ids.includes(alarm.id) && !alarm.silenced);

      await Promise.all(toSilence.map((alarm) => silenceAlarm(alarm.id).unwrap()));
    } catch (error) {
      console.error('Error when acknowledging', ids);
    }
  };

  const onSilence = async (alarmIds) => {
    const ids = Array.isArray(alarmIds) ? alarmIds : [alarmIds];
    try {
      await silenceAlarm(ids).unwrap();
    } catch (error) {
      console.error('Error when silencing', ids);
    }
  };

  const onSelectAlarm = (alarmId) => {
    if (!selectedAlarms.includes(alarmId)) {
      setSelectedAlarms([...selectedAlarms, alarmId]);
    } else {
      setSelectedAlarms(selectedAlarms.filter((id) => id !== alarmId));
    }
  };

  const onSelectAll = () => {
    if (!alarms.alarms?.length) return;
    if (selectedAlarms.length !== alarms.alarms.length) {
      const alarmIds = alarms.alarms.map((alarm) => alarm.id);
      setSelectedAlarms(alarmIds);
    } else {
      setSelectedAlarms([]);
    }
  };

  const onAcknowledgeSelected = () => {
    if (!selectedAlarms.length) return;
    onAcknowledge(selectedAlarms);
    setSelectedAlarms([]);
  };

  const onSilenceSelected = () => {
    if (!selectedAlarms.length) return;

    const onlyUnsilencedAlarms = selectedAlarms.filter((alarmId) => {
      const alarm = alarms.alarms.find((a) => a.id === alarmId);
      return alarm && !alarm.silenced;
    });

    if (!onlyUnsilencedAlarms.length) return;

    onSilence(onlyUnsilencedAlarms);
    setSelectedAlarms(selectedAlarms.filter((id) => !onlyUnsilencedAlarms.includes(id)));
  };

  // Table columns configuration (desktop)
  const columns = useMemo(
    () => [
      {
        header: '',
        accessorKey: 'select',
        enableSorting: false,
        size: 80,
        Cell: ({ row }) => {
          const isSelected = selectedAlarms.includes(row.original.id);
          return (
            <div
              className={classNames(
                isSelected ? 'circle-fill' : 'circle-border',
                'center-content',
                'ms-2',
              )}
              style={{
                '--radius': isSelected ? '16px' : '15px',
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSelectAlarm(row.original.id);
              }}
            >
              {isSelected && <BsCheck color={'black'} size={25} />}
            </div>
          );
        },
      },
      {
        header: 'Current State',
        accessorKey: 'state',
        Cell: ({ row, cell }) => {
          const state = cell.getValue();
          const alarm = row.original;
          return (
            <div className="d-flex align-items-center gap-2 text-white fw-bold fs-6">
              {state}
              {!!alarm.silenced && (
                <FaBellSlash
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSilence(alarm.id);
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        header: 'Source',
        accessorKey: 'source',
        Cell: ({ cell }) => {
          const source = cell.getValue();
          const imgSrc = iconMap[source] || iconMap.Other;
          return (
            <div className="d-flex align-items-center gap-2 text-white fs-6">
              <div
                className="circle-fill circle-border center-content"
                style={{
                  '--fill-color': theme.primaryShadow,
                  '--border-color': theme.fontColor,
                  '--radius': '22px',
                }}
              >
                <img src={imgSrc} alt="source-icon" size={25} />
              </div>
              {sourceStringCorrection(source)}
            </div>
          );
        },
      },
      {
        header: 'Device',
        accessorKey: 'device',
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue()}</div>,
      },
      {
        header: 'Type',
        accessorKey: 'type',
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue()}</div>,
      },
      {
        header: 'Activity',
        accessorKey: 'activity',
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue()}</div>,
      },
      {
        header: 'Status',
        accessorKey: 'status',
        Cell: ({ cell }) => <div className="text-white fs-6">{cell.getValue()}</div>,
      },
      {
        header: '',
        accessorKey: 'actions',
        enableSorting: false,
        Cell: ({ row }) => {
          const alarm = row.original;
          return (
            <EllipsisMenu
              options={[
                {
                  component: (
                    <AcknowledgeButton
                      onClick={() => onAcknowledge(alarm.id)}
                      color={theme.primary}
                      fontColor={theme.fontColor}
                    />
                  ),
                },
                {
                  component: (
                    <SilenceButton
                      onClick={() => onSilence(alarm.id)}
                      color={theme.primary}
                      fontColor={theme.fontColor}
                    />
                  ),
                },
              ]}
              closeOption
            />
          );
        },
      },
    ],
    [alarms.alarms, selectedAlarms, theme],
  );

  return (
    <AlarmsContentWrapper
      title="Pending Alarms"
      pageIndex={alarms.page}
      maxPageIndex={Math.floor((alarms.totalCount - 1) / alarms.limit)} // index-based
      onPageChanged={(_page) => {
        setSelectedAlarms([]);
        alarms.setPage(_page);
      }}
      onLimitChanged={(_limit) => {
        alarms.setLimit(_limit);
      }}
      onSearchChanged={(_search) => {
        alarms.setSearch(_search);
      }}
      silenceControlsEnabled
      allSelected={!!alarms.alarms?.length && selectedAlarms.length === alarms.alarms.length}
      onSelectAll={onSelectAll}
      onAcknowledge={onAcknowledgeSelected}
      onSilence={onSilenceSelected}
    >
      {/* Desktop */}
      <div className="d-none d-lg-block h-100">
        {alarms.isLoading ? (
          <div className="d-flex justify-content-center align-items-center h-100 text-white fs-4">
            <WidgetLoadingAnimation />
          </div>
        ) : (
          <BaseTable
            columns={columns}
            rows={rows}
            expandable
            altRowSettings={{
              alternate: true,
              color1: '#222a2d',
              color2: '#2C373B',
            }}
            customStyles={{
              cell: {
                borderBottom: '1px solid #56577A',
                cursor: 'pointer',
              },
            }}
            muiTablePaperProps={{
              sx: { height: '100%', display: 'flex', flexDirection: 'column' },
            }}
            muiTableContainerProps={{ sx: { flex: 1, overflowY: 'auto' } }}
            dropdownMenu={(alarm) => (
              <div className="w-100 d-flex flex-column user-select-text">
                <div className="fw-bold fs-5">{alarm.headline}</div>
                <div className="p-3">
                  {alarm.message
                    .trim()
                    .split('*')
                    .map((line, index) => (line ? <li key={index}>{line}</li> : null))}
                </div>
                <div
                  className="p-3 d-grid gap-3 align-items-center"
                  style={{
                    '--grid-size': `${Array(Math.floor(Math.sqrt(Object.keys(alarm.info).length)))
                      .fill('1fr')
                      .join(' ')}`,
                    gridTemplateRows: 'var(--grid-size)',
                    gridTemplateColumns: 'var(--grid-size)',
                  }}
                >
                  {Object.keys(alarm.info)
                    .filter((key) => !['timezone', 'popaffected'].includes(key.toLowerCase()))
                    .sort(keySort)
                    .map((key) => (
                      <div key={key}>{getKeyValue(key, alarm)}</div>
                    ))}
                  <div>
                    <span className="fw-bold">Alarm ID: </span>
                    {getAlarmId(alarm)}
                  </div>
                </div>
              </div>
            )}
          />
        )}
      </div>

      {/* Mobile: BaseAccordion */}
      <div className="d-block d-lg-none h-100 overflow-auto">
        {alarms.isLoading ? (
          <div className="d-flex justify-content-center align-items-center h-100 text-white fs-4">
            <WidgetLoadingAnimation />
          </div>
        ) : (
          alarms.alarms?.map((alarm, index) => (
            <div className={index !== alarms.alarms.length - 1 ? 'mb-3' : ''} key={alarm.id}>
              <BaseAccordion
                key={alarm.id}
                bodyBackground="#232B33"
                header={
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="circle-fill circle-border center-content"
                      style={{
                        '--fill-color': '#1e374f',
                        '--border-color': 'white',
                        '--radius': '22px',
                      }}
                    >
                      <img
                        src={iconMap[alarm.source] || iconMap.Other}
                        alt="source-icon"
                        size={25}
                      />
                    </div>
                    <div className="d-flex flex-column gap-1 text-white">
                      <p className="mb-0">{sourceStringCorrection(alarm.source)}</p>
                      <p className="mb-0">
                        {alarm.state === 1 ? 'Active' : 'Inactive'}{' '}
                        {!!alarm.silenced && <FaBellSlash className="ms-1" />}
                      </p>
                    </div>
                  </div>
                }
              >
                <div className="w-100 d-flex flex-column gap-2 text-white">
                  <p className="mb-0">
                    <span className="fw-bold">Source:</span> {alarm.source}
                  </p>
                  {alarm.device && (
                    <p className="mb-0">
                      <span className="fw-bold">Device ID:</span> {alarm.device}
                    </p>
                  )}
                  <p className="mb-0">
                    <span className="fw-bold">Activity:</span>{' '}
                    {new Date(alarm.activity).toLocaleString()}
                  </p>
                  <p className="mb-0">
                    <span className="fw-bold">Status:</span> {timeSinceActivity(alarm.activity)}
                  </p>
                  <p className="fw-bold mb-1">{shortenHeadline(alarm.headline)}</p>
                  <ul className="mb-2">
                    <li>{alarm.message}</li>
                  </ul>
                  <p className="mb-0">
                    <span className="fw-bold">Alert Type:</span> {alarm.info.type}
                  </p>
                  {alarm.info.startTime && (
                    <p className="mb-0">
                      <span className="fw-bold">Start Time:</span>{' '}
                      {new Date(alarm.info.startTime).toLocaleString()}
                    </p>
                  )}
                  {alarm.info.expireTime && (
                    <p className="mb-0">
                      <span className="fw-bold">Expire Time:</span>{' '}
                      {new Date(alarm.info.expireTime).toLocaleString()}
                    </p>
                  )}
                  {alarm.info.location && (
                    <p className="mb-0">
                      <span className="fw-bold">Location:</span> {alarm.info.location}
                    </p>
                  )}
                  {alarm.info.NWSOffice && (
                    <p className="mb-0">
                      <span className="fw-bold">Issuing Office:</span> {alarm.info.NWSOffice}
                    </p>
                  )}
                  <p className="mb-0">
                    <span className="fw-bold">Alarm ID:</span> {getAlarmId(alarm)}
                  </p>
                  {alarm.info.Latitude && (
                    <p className="mb-0">
                      <span className="fw-bold">Latitude:</span> {alarm.info.Latitude}
                    </p>
                  )}
                  {alarm.info.Longitude && (
                    <p className="mb-0">
                      <span className="fw-bold">Longitude:</span> {alarm.info.Longitude}
                    </p>
                  )}

                  {/* Extra info grid */}
                  {Object.keys(alarm.info)
                    .filter((key) => !['timezone', 'popaffected'].includes(key.toLowerCase()))
                    .sort(keySort)
                    .map((key) => (
                      <div key={key}>{getKeyValue(key, alarm)}</div>
                    ))}

                  {/* Mobile actions */}
                  <div className="d-flex gap-2 mt-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-light flex-fill"
                      onClick={() => onAcknowledge(alarm.id)}
                    >
                      Acknowledge
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-light flex-fill"
                      onClick={() => onSilence(alarm.id)}
                    >
                      Silence
                    </button>
                  </div>
                </div>
              </BaseAccordion>
            </div>
          ))
        )}
      </div>
    </AlarmsContentWrapper>
  );
}
